import { useCallback, useEffect, useRef } from 'react';

import intercom, { hide, show, shutdown } from '@intercom/messenger-js-sdk';

import { useAuth } from '../../behaviors';

const INTERCOM_APP_ID = 'qsl2vvw9';

interface UseIntercomOptions {
  enabled?: boolean;
}

interface IntercomIdentity {
  userId: string;
  userHash: string;
}

async function fetchIntercomIdentityHash(
  accessToken: string,
): Promise<IntercomIdentity | undefined> {
  try {
    const response = await fetch(
      'https://onboarding.confidence.dev/v1/intercom/identity',
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    if (!response.ok) return undefined;
    return await response.json();
  } catch {
    return undefined;
  }
}

export const useIntercom = ({ enabled = true }: UseIntercomOptions = {}) => {
  const bootedRef = useRef(false);
  const identityCacheRef = useRef<IntercomIdentity>();
  const { getAccessTokenSilently } = useAuth();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    if (bootedRef.current) {
      return undefined;
    }

    intercom({
      app_id: INTERCOM_APP_ID,
      hide_default_launcher: true,
    });
    bootedRef.current = true;

    return () => {
      shutdown();
      bootedRef.current = false;
    };
  }, [enabled]);

  const openChat = useCallback(
    async (user?: { name: string; email: string }) => {
      if (!enabled) return;

      if (user) {
        let identity = identityCacheRef.current;
        if (!identity) {
          try {
            const token = await getAccessTokenSilently();
            identity = await fetchIntercomIdentityHash(token);
            if (identity) {
              identityCacheRef.current = identity;
            }
          } catch {
            // Fall through without identity verification
          }
        }

        intercom({
          app_id: INTERCOM_APP_ID,
          hide_default_launcher: true,
          user_id: identity?.userId ?? user.email,
          name: user.name,
          email: user.email,
          ...(identity?.userHash && { user_hash: identity.userHash }),
        });
      }

      show();
    },
    [enabled, getAccessTokenSilently],
  );

  const closeChat = useCallback(() => {
    if (!enabled) return;
    hide();
  }, [enabled]);

  const toggleChat = useCallback(() => {
    if (!enabled) return;
    show();
  }, [enabled]);

  return {
    openChat,
    closeChat,
    toggleChat,
  };
};
